import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './OptionScope.css';

function fmtIdx(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPnl(n) {
  const v = Number(n) || 0;
  const sign = v > 0 ? '+' : '';
  return `${sign}₹${Math.abs(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtStrike(n) {
  return Number(n || 0).toLocaleString('en-IN');
}

function IndexPill({ card }) {
  if (!card) return null;
  return (
    <div className="os-index">
      <span className="os-index-label">{card.label}</span>
      <span className="os-index-price">{fmtIdx(card.price)}</span>
      <span className={`os-index-chg ${card.up ? 'up' : 'down'}`}>
        {card.changePts >= 0 ? '+' : ''}
        {fmtIdx(card.changePts)}
        {' '}
        ({card.changePct >= 0 ? '+' : ''}
        {fmtIdx(card.changePct)}%)
      </span>
    </div>
  );
}

function OiBar({ value, max, tone }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="os-oi-cell">
      <span>{fmtOiLabel(value)}</span>
      <div className="os-oi-track">
        <div className={`os-oi-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function fmtOiLabel(n) {
  const v = Math.abs(Number(n) || 0);
  if (v >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(Math.round(n || 0));
}

function PcrGauge({ pcr }) {
  // Map PCR ~0.6–1.6 onto 0–100% (bearish → bullish)
  const pct = Math.max(4, Math.min(96, ((Number(pcr) || 1) - 0.55) / 1.1 * 100));
  return (
    <div className="os-card os-pcr">
      <h3>Put/Call Ratio (PCR)</h3>
      <div className="os-pcr-value">{Number(pcr || 0).toFixed(2)}</div>
      <div className="os-pcr-gauge">
        <div className="os-pcr-gradient" />
        <div className="os-pcr-marker" style={{ left: `${pct}%` }} />
      </div>
      <div className="os-pcr-labels">
        <span>Bearish</span>
        <span>Neutral</span>
        <span>Bullish</span>
      </div>
    </div>
  );
}

function AutoSideCard({ card }) {
  if (!card) return null;
  const tone = card.side === 'CE' ? 'call' : 'put';
  return (
    <div className={`os-auto-side ${tone} ${card.active ? 'active' : ''} ${card.ready ? 'ready' : ''}`}>
      <div className="os-auto-side-top">
        <strong>{card.label}</strong>
        <em>{card.state}</em>
      </div>
      <div className="os-auto-side-body">
        <span>Strike {fmtStrike(card.strike)}</span>
        <span>LTP {card.ltp != null ? card.ltp : '—'}</span>
      </div>
    </div>
  );
}

function BigNiftyChart({ chart }) {
  const rows = chart?.candles || [];
  const w = 1000;
  const h = 360;
  const padL = 56;
  const padR = 16;
  const padT = 18;
  const padB = 28;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  if (!rows.length) {
    return (
      <section className="os-card os-big-chart">
        <header className="os-chart-head">
          <div>
            <h2>NIFTY · Live chart</h2>
            <p>5-minute candles</p>
          </div>
          <strong className="os-chart-spot">{fmtIdx(chart?.spot)}</strong>
        </header>
        <div className="os-chart-empty">Loading live NIFTY chart…</div>
      </section>
    );
  }

  const lows = rows.map((c) => c.low);
  const highs = rows.map((c) => c.high);
  const levels = [chart.support, chart.resistance, chart.atm, chart.maxPain, chart.spot]
    .filter((n) => Number(n) > 0);
  const min = Math.min(...lows, ...levels);
  const max = Math.max(...highs, ...levels);
  const range = max - min || 1;
  const y = (v) => padT + (1 - (v - min) / range) * plotH;
  const barW = plotW / rows.length;
  const last = rows[rows.length - 1];
  const up = (chart.dayChangePts || 0) >= 0;
  const linePts = rows
    .map((c, i) => `${padL + i * barW + barW / 2},${y(c.close)}`)
    .join(' ');

  const levelLines = [
    { key: 'atm', label: 'ATM', value: chart.atm, color: '#fbbf24' },
    { key: 'sup', label: 'Support', value: chart.support, color: '#34d399' },
    { key: 'res', label: 'Resistance', value: chart.resistance, color: '#f87171' },
    { key: 'pain', label: 'Max Pain', value: chart.maxPain, color: '#94a3b8' }
  ].filter((l) => Number(l.value) > 0);

  const yTicks = 5;
  const tickVals = Array.from({ length: yTicks }, (_, i) => min + (range * i) / (yTicks - 1));

  return (
    <section className="os-card os-big-chart">
      <header className="os-chart-head">
        <div>
          <h2>NIFTY · Live chart</h2>
          <p>5m candles · {rows.length} bars · ATM / Support / Resistance overlays</p>
        </div>
        <div className="os-chart-price">
          <strong className={last.bull ? 'up' : 'down'}>{fmtIdx(chart.spot || last.close)}</strong>
          <span className={up ? 'up' : 'down'}>
            {up ? '+' : ''}
            {fmtIdx(chart.dayChangePts)} ({up ? '+' : ''}
            {fmtIdx(chart.dayChangePct)}%)
          </span>
        </div>
      </header>
      <svg viewBox={`0 0 ${w} ${h}`} className="os-big-chart-svg" role="img" aria-label="NIFTY candlestick chart">
        {tickVals.map((v) => (
          <g key={`yt-${v}`}>
            <line x1={padL} y1={y(v)} x2={w - padR} y2={y(v)} className="os-chart-grid" />
            <text x={padL - 8} y={y(v) + 4} className="os-chart-ylabel" textAnchor="end">
              {Math.round(v).toLocaleString('en-IN')}
            </text>
          </g>
        ))}
        {levelLines.map((l) => (
          <g key={l.key}>
            <line
              x1={padL}
              y1={y(l.value)}
              x2={w - padR}
              y2={y(l.value)}
              stroke={l.color}
              strokeWidth="1.2"
              strokeDasharray="5 4"
              opacity="0.85"
            />
            <text x={w - padR - 4} y={y(l.value) - 4} fill={l.color} className="os-chart-level" textAnchor="end">
              {l.label} {Math.round(l.value).toLocaleString('en-IN')}
            </text>
          </g>
        ))}
        <polyline points={linePts} className="os-chart-line" fill="none" />
        {rows.map((c, i) => {
          const x = padL + i * barW + barW * 0.18;
          const bw = Math.max(2, barW * 0.64);
          const color = c.bull ? '#2dd4bf' : '#f87171';
          const yO = y(c.open);
          const yC = y(c.close);
          return (
            <g key={`c-${c.t || i}`}>
              <line x1={x + bw / 2} y1={y(c.high)} x2={x + bw / 2} y2={y(c.low)} stroke={color} strokeWidth="1.2" />
              <rect
                x={x}
                y={Math.min(yO, yC)}
                width={bw}
                height={Math.max(2, Math.abs(yO - yC))}
                fill={color}
                rx="1"
              />
            </g>
          );
        })}
      </svg>
      <div className="os-chart-legend">
        <span className="call">Call / up</span>
        <span className="put">Put / down</span>
        <span>Yellow = ATM · Green = Support · Red = Resistance</span>
      </div>
    </section>
  );
}

export default function OptionScope({ pollMs = 8000 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data: payload } = await axios.get('/api/options/scope', { timeout: 20000 });
      if (payload?.ok === false) {
        setError(payload.error || 'OptionScope failed');
        return;
      }
      setData(payload);
      setError(null);
    } catch (e) {
      setError(e.message || 'Cannot load OptionScope');
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, pollMs);
    return () => clearInterval(t);
  }, [refresh, pollMs]);

  const chain = data?.chain;
  const indices = data?.indices;
  const levels = data?.keyLevels;
  const pnl = data?.todayPnl;
  const auto = data?.auto;
  const chart = data?.chart;

  return (
    <div className="os-app">
      <header className="os-header">
        <div className="os-brand">
          <div className="os-logo" aria-hidden>Ω</div>
          <div>
            <h1>OptionScope</h1>
            <p>{data?.subtitle || 'Fully auto · Buy Call / Buy Put · 0% loss only'}</p>
          </div>
        </div>
        <div className="os-indices">
          <IndexPill card={indices?.nifty} />
          <IndexPill card={indices?.bankNifty} />
          <IndexPill card={indices?.vix} />
        </div>
      </header>

      <div className="os-auto-bar">
        <div className="os-auto-pills">
          <span className="os-pill live">FULLY AUTO</span>
          <span className="os-pill zero">0% LOSS ONLY</span>
          <span className="os-pill status">{(auto?.status || 'idle').replace(/_/g, ' ')}</span>
        </div>
        <p className="os-auto-action">{auto?.lastAction || 'Arming auto trader…'}</p>
        <div className="os-auto-sides">
          <AutoSideCard card={auto?.buyCall} />
          <AutoSideCard card={auto?.buyPut} />
        </div>
        {auto?.position && (
          <p className="os-auto-hold">
            Open {auto.position.side} {fmtStrike(auto.position.strike)}
            {' · '}
            <span className={auto.position.unrealized >= 0 ? 'up' : 'down'}>
              {fmtPnl(auto.position.unrealized)}
            </span>
            {' · '}
            {auto.position.holdNote}
          </p>
        )}
      </div>

      <BigNiftyChart chart={chart} />

      {error && <div className="os-error">{error}</div>}

      <div className="os-grid">
        <section className="os-card os-chain">
          <header className="os-chain-head">
            <h2>
              {chain?.underlying || 'NIFTY'} Option Chain
              <span> · Expiry {chain?.expiryLabel || '—'}</span>
            </h2>
            <span className="os-chain-meta">OI in lots · updated {data?.updatedAt || '—'}</span>
          </header>

          <div className="os-table-wrap">
            <table className="os-table">
              <thead>
                <tr>
                  <th>OI</th>
                  <th>CHG OI</th>
                  <th>IV</th>
                  <th>LTP</th>
                  <th>Δ</th>
                  <th className="os-strike-col">STRIKE</th>
                  <th>Δ</th>
                  <th>LTP</th>
                  <th>IV</th>
                  <th>CHG OI</th>
                  <th>OI</th>
                </tr>
                <tr className="os-side-row">
                  <th colSpan={5} className="os-calls-label">CALLS</th>
                  <th />
                  <th colSpan={5} className="os-puts-label">PUTS</th>
                </tr>
              </thead>
              <tbody>
                {(chain?.rows || []).map((row) => (
                  <tr key={row.strike} className={row.atm ? 'atm' : ''}>
                    <td>
                      <OiBar value={row.call.oi} max={chain.maxCallOi} tone="call" />
                    </td>
                    <td className={row.call.chgOi >= 0 ? 'up' : 'down'}>{row.call.chgOiLabel}</td>
                    <td>{row.call.iv}</td>
                    <td className="os-ltp-call">{row.call.ltp}</td>
                    <td>{row.call.delta}</td>
                    <td className="os-strike-col">
                      {fmtStrike(row.strike)}
                      {row.atm ? <em className="os-atm-tag">ATM</em> : null}
                    </td>
                    <td>{row.put.delta}</td>
                    <td className="os-ltp-put">{row.put.ltp}</td>
                    <td>{row.put.iv}</td>
                    <td className={row.put.chgOi >= 0 ? 'up' : 'down'}>{row.put.chgOiLabel}</td>
                    <td>
                      <OiBar value={row.put.oi} max={chain.maxPutOi} tone="put" />
                    </td>
                  </tr>
                ))}
                {!chain?.rows?.length && (
                  <tr>
                    <td colSpan={11} className="os-empty">Loading live chain…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <aside className="os-side">
          <PcrGauge pcr={data?.pcr} />

          <div className="os-card os-levels">
            <h3>Key Levels</h3>
            <ul>
              <li>
                <span>Max Pain</span>
                <strong>{fmtStrike(levels?.maxPain)}</strong>
              </li>
              <li>
                <span>Support (highest Put OI)</span>
                <strong className="up">{fmtStrike(levels?.support)}</strong>
              </li>
              <li>
                <span>Resistance (highest Call OI)</span>
                <strong className="down">{fmtStrike(levels?.resistance)}</strong>
              </li>
              <li>
                <span>India VIX</span>
                <strong>{fmtIdx(levels?.vix)}</strong>
              </li>
              <li>
                <span>Loss mode</span>
                <strong className="up">0% only</strong>
              </li>
            </ul>
          </div>

          <div className="os-card os-pnl">
            <h3>Today&apos;s P&amp;L</h3>
            <div className={`os-pnl-total ${(pnl?.total || 0) >= 0 ? 'up' : 'down'}`}>
              {fmtPnl(pnl?.total)}
            </div>
            <p className="os-pnl-summary">{pnl?.summary || '0 open positions · 0 closed'}</p>
            <ul className="os-pnl-list">
              {(pnl?.lines || []).map((line) => (
                <li key={line.id}>
                  <span className={`os-side-badge ${line.side === 'CE' ? 'ce' : 'pe'}`}>{line.side}</span>
                  <span className="os-pnl-label">{line.label}</span>
                  <span className={line.pnl >= 0 ? 'up' : 'down'}>{fmtPnl(line.pnl)}</span>
                </li>
              ))}
              {!pnl?.lines?.length && (
                <li className="os-pnl-empty">No option trades yet today — auto Buy Call / Buy Put from 9:15 IST</li>
              )}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
